
import Vue from 'vue';
import { Component, Prop, Emit } from 'vue-property-decorator';

import Radio from '../Radio/Radio.vue';
import QButton from '../Button/Button.vue';
import { spacings } from '../Token/token';
import { Spacer } from '../Spacings';

/**
 * Radio Button Group.
 *
 * Create selection form with the radio button.
 *
 * There is another two emits:
 *
 * - `change`: Handle when user change value.
 * - `input`: Handle when user change value.
 */
export default Vue.extend({
  name: 'RadioGroup',
  inheritAttrs: false,
  components: {
    Radio,
    QButton,
    Spacer
  },
  props: {
    /**
     * Value of the Radio.<br />
     * When using v-model, the props will be ignored.
     */
    value: {
      type: [String, Boolean],
      default: '',
      required: true
    },
    /**
     * Radio Button options list.<br />
     * Model of the list:
     *
     * - label: label of the option.
     * - value: an unique identifier value.
     */
    items: {
      type: Array,
      default: () => [],
      required: true
    },
    /**
     * Variant of the Radio Button.
     */
    variant: {
      type: String,
      default: 'vertical'
    },
    /**
     * Radio button style.<br />
     * Possible value: `radio` | `button`.
     */
    radioStyle: {
      type: String,
      default: 'radio'
    },
    /**
     * Make radio button disabled.
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Error state of Radio Button.
     */
    error: {
      type: Boolean,
      default: false
    },
    /**
     * Gutter of each Radio Button.
     */
    gutter: {
      type: String,
      default: 'xs'
    },
    /**
     * Custom CSS Radio Button type `button`.
     */
    customButtonStyle: {
      type: String,
      default: ''
    },
    /**
     * Text variant of Radio Button.
     */
    textVariant: {
      type: String,
      default: 'ui-baseline'
    },
    /**
     * Button variant when selected of Radio Button.
     */
    buttonVariant: {
      type: String,
      default: 'outline-primary'
    },
    /**
     * Button variant when not selected of Radio Button.
     */
    buttonNotSelectedVariant: {
      type: String,
      default: 'outline-radio-inactive'
    },
    /**
     * Required props.
     */
    required: {
      type: Boolean,
      default: false
    }
  },
  created() {
    if (this.required) {
      const valueChecked = (this.items as any[]).find(option => option.value === this.value);
      /**
       * Handle error state for radio button
       * @event inputState
       * @type {function}
       */
      this.$emit('inputState', valueChecked ? true : null);
    }
  },
  computed: {
    computedGrid() {
      if (this.variant.indexOf('grid') > -1) {
        return {
          gridGap: `${spacings[this.gutter]}px` || '8px'
        };
      }
      return {};
    }
  },
  methods: {
    input(value: string) {
      /**
       * Handle emit input
       * @event input
       * @type {function}
       */
      this.$emit('input', value);
    },
    change(value: string) {
      /**
       * Handle emit change
       * @event change
       * @type {function}
       */
      this.$emit('change', value);
    },
    handleChangeRadio(value: string) {
      if (!this.disabled) {
        this.input(value);
        this.change(value);
        if (this.required) {
          this.$emit('inputState', true);
        }
      }
    },
    mapVariantButton(variant: string) {
      let mapResult = this.buttonVariant;
      switch (variant) {
        case 'horizontal-small':
          mapResult = 'primary';
          break;
        default:
          break;
      }
      return mapResult;
    }
  }
});
